import { StyleSheet, Platform } from 'react-native';
import { FontFamily } from '../../constants/fonts';
import { AdminColors } from '../Admin/constants/adminColors';

// ─── Status badge styles ──────────────────────────────────────────────────────

export const badge = StyleSheet.create({
  wrap: {
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 3,
    alignSelf: 'flex-start',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '100%',
    ...Platform.select({
      web: {
        wordWrap: 'break-word' as any,
        overflowWrap: 'break-word' as any,
      },
    }),
  },
  text: { fontSize: 11, fontWeight: '700', fontFamily: FontFamily.bold, flexWrap: 'wrap' },
  green: { backgroundColor: '#CAF3C6' },
  red: { backgroundColor: '#FFE5DE' },
  greenText: { color: '#0C420B' },
  redText: { color: '#AB2B09' },
});

// ─── Empty-state styles ───────────────────────────────────────────────────────

export const es = StyleSheet.create({
  wrap: { paddingVertical: 46, paddingHorizontal: 24, alignItems: 'center', gap: 12 },
  title: { fontSize: 14, color: '#9E9E9E', textAlign: 'center', fontFamily: FontFamily.regular },
  btn: { backgroundColor: '#005CBC', borderRadius: 100, paddingHorizontal: 20, paddingVertical: 8, marginTop: 4 },
  btnText: { color: '#fff', fontSize: 14, fontWeight: '700', fontFamily: FontFamily.bold },
});

// ─── Mobile-card styles ───────────────────────────────────────────────────────

export const mob = StyleSheet.create({
  card: { backgroundColor: '#fff', borderRadius: 10, padding: 14, borderWidth: 1, borderColor: '#F0F0F0', gap: 6, ...Platform.select({ ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3 }, android: { elevation: 1 } }) },
  top: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 },
  customer: { fontSize: 14, fontWeight: '700', color: '#1A1A1A', flex: 1, fontFamily: FontFamily.bold },
  row: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  meta: { fontSize: 13, color: '#9E9E9E', fontFamily: FontFamily.regular },
  val: { color: '#1A1A1A', fontWeight: '600', fontFamily: FontFamily.medium },
});

// ─── Main styles ──────────────────────────────────────────────────────────────

export const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: AdminColors.bgPage },
  scroll: { flex: 1 },
  scrollContent: { padding: 24, paddingBottom: 64, gap: 20 },

  readNote: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: '#DAEEFA', borderWidth: 1, borderColor: '#C6DCFB',
    borderRadius: 8, padding: 10, paddingHorizontal: 14,
  },
  readNoteText: { fontSize: 13, fontWeight: '500', color: '#01346F', fontFamily: FontFamily.medium, flex: 1 },

  // Popover
  popoverBackdrop: { position: 'absolute', inset: 0, zIndex: 200 },
  popoverWrap: { position: 'absolute', top: 300, left: 100, zIndex: 201 },

  // SSCC Card
  ssccCard: { backgroundColor: '#fff', borderRadius: 8, padding: 24, gap: 16, position: 'relative', ...Platform.select({ web: { overflow: 'visible', zIndex: 3 } as any, ios: { shadowColor: '#005EBC', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.08, shadowRadius: 24 }, android: { elevation: 2 } }) },
  // Card header row with title and toggle
  ssccCardHeadRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' },
  ssccHeader: { gap: 4 },
  ssccTitle: { fontSize: 20, lineHeight: 28, fontWeight: '700', color: '#1A1A1A', fontFamily: FontFamily.bold },
  ssccSubtitle: { fontSize: 13, color: '#616161', fontFamily: FontFamily.regular },
  ssccRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', zIndex: 100 },
  ssccBox: { flex: 1, minWidth: 240, maxWidth: 430, gap: 8 },
  // Field label above input
  ssccFieldLabel: { fontSize: 14, fontWeight: '700', color: '#1A1A1A', fontFamily: FontFamily.bold, lineHeight: 20 },
  ssccRequired: { color: '#C12C01' },
  ssccWrap: { position: 'relative', flexDirection: 'row', alignItems: 'center' },
  ssccInput: { flex: 1, height: 40, borderWidth: 1, borderColor: '#D0D0D0', borderRadius: 6, paddingHorizontal: 12, paddingRight: 82, fontSize: 14, color: '#1A1A1A', letterSpacing: 0.5, fontFamily: FontFamily.regular },
  goArrow: { position: 'absolute', right: 5, width: 32, height: 32, borderRadius: 16, backgroundColor: '#F5F5F5', alignItems: 'center', justifyContent: 'center' },
  goArrowReady: { backgroundColor: '#027C06' },
  // + Add button (appears to the left of the go arrow inside the input wrap)
  addBtn: { position: 'absolute', right: 42, width: 32, height: 32, borderRadius: 16, backgroundColor: '#F5F5F5', alignItems: 'center', justifyContent: 'center' },
  addBtnReady: { backgroundColor: '#034895' },
  // Added SSCC chip list
  ssccChipList: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 },
  ssccChip: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#EFF5FC', borderWidth: 1, borderColor: '#C6DCFB', borderRadius: 6, paddingHorizontal: 10, paddingVertical: 5 },
  ssccChipText: { fontSize: 13, color: '#034895', fontFamily: FontFamily.regular, letterSpacing: 0.3 },
  ssccChipRemove: { width: 18, height: 18, borderRadius: 9, backgroundColor: '#034895', alignItems: 'center', justifyContent: 'center' },
  ssccMeta: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 10 },
  counter: { fontSize: 12, fontWeight: '500', color: '#838383', fontFamily: FontFamily.medium },
  counterDone: { color: '#027C06', fontWeight: '700' },
  ssccErr: { fontSize: 12, color: '#C12C01', fontWeight: '500', fontFamily: FontFamily.medium },
  // Scan button column — marginTop aligns button with the input (label lineHeight 20 + gap 8 = 28)
  scanCol: { flexDirection: 'column', gap: 6, marginTop: 28 },
  scanHint: { fontSize: 12, color: '#838383', fontFamily: FontFamily.regular },
  scanBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, height: 40, paddingHorizontal: 16, borderRadius: 24, borderWidth: 1, borderColor: '#034895', backgroundColor: '#fff' },
  scanBtnDisabled: { borderColor: '#D0D0D0', backgroundColor: AdminColors.bgPage },
  scanBtnText: { fontSize: 16, fontWeight: '700', color: '#034895', fontFamily: FontFamily.bold },
  scanBtnTextDisabled: { color: '#838383' },

  // SSCC / Customer mode switcher toggle (top-right of entry card)
  switcher: { flexDirection: 'row', backgroundColor: '#F5F5F5', borderRadius: 8, padding: 2, alignSelf: 'flex-start' },
  swOpt: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 7, alignItems: 'center', justifyContent: 'center' },
  swOptActive: { backgroundColor: '#fff', ...Platform.select({ ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.08, shadowRadius: 2 }, android: { elevation: 1 } }) },
  swOptText: { fontSize: 13, fontWeight: '600', color: '#616161', fontFamily: FontFamily.medium },
  swOptTextActive: { color: '#034895', fontWeight: '700', fontFamily: FontFamily.bold },

  // Customer typeahead dropdown
  taWrap: { position: 'relative', zIndex: 999, overflow: 'visible' as any },
  taDropdown: {
    position: 'absolute', top: 44, left: 0, right: 0, zIndex: 9999,
    backgroundColor: '#fff', borderRadius: 8, borderWidth: 1, borderColor: '#EBEBEB',
    maxHeight: 240,
    ...Platform.select({ ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.12, shadowRadius: 12 }, android: { elevation: 6 } }),
  },
  taList: { maxHeight: 240 },
  taOption: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 10, gap: 8, borderBottomWidth: 1, borderBottomColor: '#F5F5F5' },
  taCofCol: { fontSize: 13, fontWeight: '700', color: '#034895', fontFamily: FontFamily.bold, minWidth: 60 },
  taSep: { fontSize: 13, color: '#838383', fontFamily: FontFamily.regular },
  taName: { fontSize: 13, color: '#1A1A1A', fontFamily: FontFamily.regular, flex: 1 },
  taEmpty: { fontSize: 13, color: '#838383', fontFamily: FontFamily.regular, padding: 14, textAlign: 'center' },

  // Table card
  tableCard: { backgroundColor: '#fff', borderRadius: 8, overflow: 'hidden', ...Platform.select({ ios: { shadowColor: '#005EBC', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.08, shadowRadius: 24 }, android: { elevation: 2 } }) },
  tableTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingTop: 12, paddingBottom: 12, flexWrap: 'wrap', gap: 10 },
  tableSummary: { fontSize: 13, fontWeight: '600', color: '#616161', fontFamily: FontFamily.medium },
  tableSummaryBold: { fontWeight: '700', color: '#1A1A1A', fontFamily: FontFamily.bold },
  tableTools: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  searchWrap: { flexDirection: 'row', alignItems: 'center', gap: 6, height: 34, paddingHorizontal: 10, borderWidth: 1, borderColor: '#D0D0D0', borderRadius: 6, minWidth: 230 },
  searchInput: { flex: 1, fontSize: 14, color: '#1A1A1A', fontFamily: FontFamily.regular },
  clearAllBtn: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 24 },
  clearAllText: { fontSize: 14, fontWeight: '700', color: '#005CBC', fontFamily: FontFamily.bold },

  // Quick pills
  pillsScroll: { maxHeight: 52 },
  pills: { flexDirection: 'row', gap: 8, paddingHorizontal: 16, paddingTop: 16, paddingBottom: 0 },
  pill: { flexDirection: 'row', alignItems: 'center', gap: 7, height: 32, paddingHorizontal: 14, borderRadius: 999, borderWidth: 1.5, borderColor: '#EBEBEB', backgroundColor: '#fff' },
  pillActive: { backgroundColor: '#005CBC', borderColor: '#005CBC' },
  pillText: { fontSize: 13, fontWeight: '600', color: '#616161', fontFamily: FontFamily.medium },
  pillTextActive: { color: '#fff' },
  pillBadge: { backgroundColor: '#EFF5FC', borderRadius: 999, paddingHorizontal: 7, paddingVertical: 1 },
  pillBadgeActive: { backgroundColor: 'rgba(255,255,255,0.22)' },
  pillBadgeText: { fontSize: 11, fontWeight: '700', color: '#034895', lineHeight: 16, fontFamily: FontFamily.bold },
  pillBadgeTextActive: { color: '#fff' },

  // Desktop table
  theadRow: { flexDirection: 'row', backgroundColor: '#fff', minWidth: 980 },
  trow: { flexDirection: 'row', minWidth: 980, borderBottomWidth: 1, borderBottomColor: '#F5F5F5' },
  trowAlt: { backgroundColor: '#FAFAFA' },
  td: { flex: 1, height: 48, paddingHorizontal: 12, justifyContent: 'center' },
  tdText: { fontSize: 14, color: '#1A1A1A', fontFamily: FontFamily.regular },
  tdMono: { fontSize: 13, letterSpacing: 0.3, fontFamily: FontFamily.regular },

  // Mobile list
  mobileList: { padding: 16, gap: 10 },

  // Pagination
  pagFoot: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 4, padding: 12 },
  pgBtn: { minWidth: 30, height: 30, borderRadius: 6, borderWidth: 1, borderColor: '#EBEBEB', backgroundColor: '#fff', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 8 },
  pgBtnActive: { backgroundColor: '#034895', borderColor: '#034895' },
  pgBtnDisabled: { opacity: 0.4 },
  pgBtnText: { fontSize: 13, fontWeight: '600', color: '#616161', fontFamily: FontFamily.medium },
  pgBtnActiveText: { color: '#fff' },
});
